<template>
  <div class="user-management">
    <h2>用户管理</h2>
    <el-card shadow="hover">
      <template #header>
        <div class="card-header">
          <span>用户列表</span>
        </div>
      </template>
      <div class="user-list-content">
        <!-- 搜索区域 -->
        <div class="search-area">
          <el-input
            v-model="searchForm.keyword"
            placeholder="请输入用户昵称、手机号码"
            prefix-icon="Search"
            clearable
            class="search-input"
          >
            <template #append>
              <el-button type="primary" @click="handleSearch">
                <el-icon><Search /></el-icon>
                搜索
              </el-button>
            </template>
          </el-input>
        </div>

        <!-- 数据表格 -->
        <el-table
          v-loading="loading"
          :data="userList"
          stripe
          style="width: 100%"
          class="user-table"
          :row-key="row => row.id"
        >
          <el-table-column prop="avatar" label="用户头像" width="80">
            <template #default="scope">
              <el-avatar :src="scope.row.avatar" size="small" />
            </template>
          </el-table-column>
          <el-table-column prop="nickname" label="用户昵称" />
          <el-table-column prop="phone" label="手机号码" width="150" />
          <el-table-column prop="created_at" label="注册时间" width="180" />
          <el-table-column prop="points_balance" label="积分余额" width="120" />
          <el-table-column prop="expiring_points" label="即将到期积分" width="150" />
          <el-table-column prop="member_level" label="当前会员等级" width="150" />
          <el-table-column prop="member_level_updated_at" label="升级时间" width="180" />
          <el-table-column label="会员周期内累计消费额" width="180">
            <template #default="scope">
              <div>
                <span>{{ scope.row.cycle_consumption }} 元</span>
                <el-button type="text" size="small" @click="showConsumptionRecords(scope.row, 'cycle')">
                  查看
                </el-button>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="周期内累计消费额" width="180">
            <template #header>
              <div class="label-with-tooltip">
                <span>周期内累计消费额</span>
                <el-tooltip effect="dark" content="会员在当前会员周期内的累积消费，不计算其他会员周期内的消费" placement="top">
                  <el-icon class="question-icon"><QuestionFilled /></el-icon>
                </el-tooltip>
              </div>
            </template>
            <template #default="scope">
              <div>
                <span>{{ scope.row.total_consumption }} 元</span>
                <el-button type="text" size="small" @click="showConsumptionRecords(scope.row, 'total')">
                  查看
                </el-button>
              </div>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-area">
          <el-pagination
            :current-page="pagination.currentPage"
            :page-size="pagination.pageSize"
            :page-sizes="[10, 20, 50, 100]"
            layout="total, sizes, prev, pager, next, jumper"
            :total="pagination.total"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </div>
    </el-card>

    <!-- 消费记录弹窗 -->
    <el-dialog
      v-model="consumptionDialogVisible"
      :title="consumptionDialogTitle"
      width="80%"
    >
      <div v-if="currentUser" class="consumption-records">
        <h3>{{ currentUser.nickname }} 的消费记录</h3>
        <el-table
          v-loading="consumptionLoading"
          :data="consumptionRecords"
          stripe
          style="width: 100%"
        >
          <el-table-column prop="id" label="ID" width="80" />
          <el-table-column prop="amount" label="消费金额" width="120" />
          <el-table-column prop="consumed_at" label="消费时间" width="180" />
          <el-table-column prop="order_no" label="订单号" />
          <el-table-column prop="status" label="状态" width="120" />
        </el-table>
        
        <div class="consumption-pagination" v-if="!consumptionLoading && consumptionRecords.length > 0">
          <el-pagination
            :current-page="consumptionPagination.currentPage"
            :page-size="consumptionPagination.pageSize"
            :page-sizes="[10, 20, 50, 100]"
            layout="total, sizes, prev, pager, next, jumper"
            :total="consumptionPagination.total"
            @size-change="handleConsumptionSizeChange"
            @current-change="handleConsumptionCurrentChange"
          />
        </div>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, onMounted } from 'vue';
import { ElMessage } from 'element-plus';
import { Search, QuestionFilled } from '@element-plus/icons-vue';
import * as userApi from '../../api/userManagement';

export default {
  name: 'UserManagement',
  components: {
    Search,
    QuestionFilled
  },
  setup() {
    // 搜索表单
    const searchForm = reactive({
      keyword: ''
    });

    // 加载状态
    const loading = ref(false);

    // 用户列表数据
    const userList = ref([]);

    // 分页数据
    const pagination = reactive({
      currentPage: 1,
      pageSize: 10,
      total: 0
    });
    
    // 消费记录相关
    const consumptionDialogVisible = ref(false);
    const consumptionDialogTitle = ref('消费记录');
    const consumptionLoading = ref(false);
    const currentUser = ref(null);
    const consumptionRecords = ref([]);
    const consumptionPagination = reactive({
      currentPage: 1,
      pageSize: 10,
      total: 0
    });
    const consumptionType = ref('');

    // 加载用户列表
    const loadUsers = async () => {
      loading.value = true;
      try {
        const params = {
          page: pagination.currentPage,
          per_page: pagination.pageSize,
          search: searchForm.keyword
        };
        const response = await userApi.fetchUserManagementList(params);
        if (response.code === 200) {
          userList.value = response.data.items;
          pagination.total = response.data.pagination.total;
        } else {
          ElMessage.error('获取用户列表失败');
        }
      } catch (error) {
        ElMessage.error('获取用户列表失败');
        console.error(error);
      } finally {
        loading.value = false;
      }
    };

    // 搜索用户
    const handleSearch = () => {
      pagination.currentPage = 1;
      loadUsers();
    };

    // 显示消费记录
    const showConsumptionRecords = (user, type) => {
      currentUser.value = user;
      consumptionType.value = type;
      consumptionDialogTitle.value = type === 'cycle' ? '会员周期内消费记录' : '累计消费记录';
      consumptionDialogVisible.value = true;
      consumptionPagination.currentPage = 1;
      loadConsumptionRecords();
    };
    
    // 加载消费记录
    const loadConsumptionRecords = async () => {
      if (!currentUser.value) return;
      
      consumptionLoading.value = true;
      try {
        const params = {
          page: consumptionPagination.currentPage,
          per_page: consumptionPagination.pageSize,
          type: consumptionType.value
        };
        const response = await userApi.fetchUserConsumptionRecords(currentUser.value.id, params);
        if (response.code === 200) {
          consumptionRecords.value = response.data.items;
          consumptionPagination.total = response.data.pagination.total;
        } else {
          ElMessage.error('获取消费记录失败');
        }
      } catch (error) {
        ElMessage.error('获取消费记录失败');
        console.error(error);
      } finally {
        consumptionLoading.value = false;
      }
    };
    
    // 消费记录分页大小变化
    const handleConsumptionSizeChange = (size) => {
      consumptionPagination.pageSize = size;
      loadConsumptionRecords();
    };
    
    // 消费记录当前页变化
    const handleConsumptionCurrentChange = (current) => {
      consumptionPagination.currentPage = current;
      loadConsumptionRecords();
    };

    // 分页大小变化
    const handleSizeChange = (size) => {
      pagination.pageSize = size;
      loadUsers();
    };

    // 当前页变化
    const handleCurrentChange = (current) => {
      pagination.currentPage = current;
      loadUsers();
    };

    // 组件挂载时加载数据
    onMounted(() => {
      loadUsers();
    });

    return {
      searchForm,
      loading,
      userList,
      pagination,
      consumptionDialogVisible,
      consumptionDialogTitle,
      consumptionLoading,
      currentUser,
      consumptionRecords,
      consumptionPagination,
      handleSearch,
      showConsumptionRecords,
      handleSizeChange,
      handleCurrentChange,
      handleConsumptionSizeChange,
      handleConsumptionCurrentChange
    };
  }
};
</script>

<style scoped>
.user-management {
  padding: 20px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.search-area {
  margin-bottom: 20px;
}

.search-input {
  width: 400px;
}

.user-list-content {
  margin-top: 20px;
}

.user-table {
  margin-bottom: 20px;
}

.pagination-area {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

/* 消费记录弹窗样式 */
.consumption-records {
  padding: 10px;
}

.consumption-records h3 {
  margin-bottom: 20px;
  font-size: 18px;
  color: #333;
}

.consumption-pagination {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

.label-with-tooltip {
  display: flex;
  align-items: center;
  gap: 5px;
}

.question-icon {
  font-size: 14px;
  color: #909399;
  cursor: help;
}
</style>